import React, { useMemo, useState, useRef } from "react";
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Chart from "react-apexcharts";
import { format, parseISO, eachDayOfInterval, startOfWeek, endOfWeek, getDay, subWeeks, startOfToday, endOfToday, subDays, addHours } from 'date-fns';

import { getPracticeRecords, getPhonemeSummary, getPublicSetting } from '../api/supabaseAPI';
import { supabase } from "../supabaseClient";
import Loader from "../components/Loader";
import ModernCombobox from "../components/ModernCombobox";
import "../styles/Records.css";
import "../styles/Overall.css";
import "../styles/Layout.css";

// =================================================================
// ==   Data Processing Helpers                                    ==
// =================================================================

const formatDifficultyLevel = (level, t) => {
  if (!level) return t('recordsPage.levelLabels.unknown');
  if (level === 'initial_test') return t('recordsPage.levelLabels.initialTest');
  if (level.startsWith('initial_test_')) {
    const parts = level.split('_');
    if (parts.length >= 3) {
      return t('recordsPage.levelLabels.initialTestWithProgress', { step: parts[2] });
    }
    return t('recordsPage.levelLabels.initialTest');
  }
  if (level.startsWith('Course')) {
    return t('coursesPage.courseKicker');
  }
  return level.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const processRecordsForCharts = (records, phonemeSummary, rangeDays, t) => {
  const today = startOfToday();
  const endDate = endOfToday();
  const startDate = subDays(today, Math.max(rangeDays - 1, 0));
  const yesterdayMidTime = addHours(subDays(today, 1), 12).getTime();
  const todayMidTime = addHours(today, 12).getTime();

  const initialData = {
    progressChart: {
      fullSeriesData: [],
      dashedSeriesData: [],
      minDate: startDate.getTime(),
      maxDate: endDate.getTime(),
    },
    phonemeChart: { phonemes: [], errorCounts: [] },
    heatmapChart: [],
    difficultyChart: { levels: [], counts: [] },
  };

  if (phonemeSummary) {
    initialData.phonemeChart = {
      phonemes: phonemeSummary.map(p => p.phoneme),
      errorCounts: phonemeSummary.map(p => Number(p?.err_amount ?? 0)),
    };
  }

  const practiceCountsByDay = {};
  if (records) {
    records.forEach(rec => {
      const dateStr = format(parseISO(rec.created_at), 'yyyy-MM-dd');
      practiceCountsByDay[dateStr] = (practiceCountsByDay[dateStr] || 0) + 1;
    });
  }

  const fourWeeksAgo = startOfWeek(subWeeks(today, 3), { weekStartsOn: 1 });
  const dateInterval = eachDayOfInterval({ start: fourWeeksAgo, end: endOfWeek(today, { weekStartsOn: 1 }) });

  const weeksData = {};
  dateInterval.forEach(day => {
    const weekStartDate = startOfWeek(day, { weekStartsOn: 1 });
    const weekKey = format(weekStartDate, 'yyyy-MM-dd');

    if (!weeksData[weekKey]) {
      const weekEndDate = endOfWeek(day, { weekStartsOn: 1 });
      weeksData[weekKey] = {
        name: `${format(weekStartDate, 'MMM d')} - ${format(weekEndDate, 'd')}`,
        data: Array(7).fill(null).map((_, i) => ({ x: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i], y: 0, date: null })),
      };
    }

    const dayIndex = (getDay(day) + 6) % 7;
    const dateStr = format(day, 'yyyy-MM-dd');
    weeksData[weekKey].data[dayIndex] = {
      x: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dayIndex],
      y: practiceCountsByDay[dateStr] || 0,
      date: dateStr,
    };
  });
  initialData.heatmapChart = Object.values(weeksData);

  const progressData = {};
  if (records && records.length > 0) {
    records.forEach(rec => {
      const dateStr = format(parseISO(rec.created_at), 'yyyy-MM-dd');
      if (!progressData[dateStr]) {
        progressData[dateStr] = { totalRate: 0, count: 0 };
      }
      progressData[dateStr].totalRate += rec.error_rate || 0;
      progressData[dateStr].count += 1;
    });
  }

  const fullSeriesData = eachDayOfInterval({ start: startDate, end: today }).map((day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const summary = progressData[dateStr];
    const avgRate = summary && summary.count > 0
      ? (summary.totalRate / summary.count) * 100
      : 0;

    return {
      x: addHours(day, 12).getTime(),
      y: parseFloat(avgRate.toFixed(2))
    };
  });

  const dashedSeriesData = fullSeriesData.filter(
    (point) => point.x === yesterdayMidTime || point.x === todayMidTime
  );

  initialData.progressChart = {
    fullSeriesData,
    dashedSeriesData,
    minDate: startDate.getTime(),
    maxDate: endDate.getTime(),
  };

  const difficultyCounts = {};
  if (records && records.length > 0) {
    records.forEach(rec => {
      const level = formatDifficultyLevel(rec.diffi_level, t);
      difficultyCounts[level] = (difficultyCounts[level] || 0) + 1;
    });
  }
  initialData.difficultyChart = {
    levels: Object.keys(difficultyCounts),
    counts: Object.values(difficultyCounts),
  };

  return initialData;
};

const useUserLanguage = (userId) => {
  const { data } = useQuery({
    queryKey: ['userLanguage', userId],
    queryFn: async () => {
      if (!userId) return 'en_us';
      const { data: settings } = await supabase.from('user_settings').select('language').eq('user_id', userId).single();
      return settings?.language || 'en_us';
    },
    enabled: !!userId,
    staleTime: Infinity,
  });
  return { practiceLanguage: data };
};

// =================================================================
// ==   Overall Page                                               ==
// =================================================================
const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

// Helper to extract Y index and convert to percentage
const getBackgroundPosition = (url) => {
  if (!url) return 'center center';
  const match = url.match(/background_(\d+)(?:_v\d+)?\.png(?:\?.*)?$/i);
  if (match && match[1]) {
    const yIndex = parseInt(match[1], 10);
    const percentage = Math.min(100, Math.max(0, (yIndex / 1000) * 100));
    return `center ${percentage}%`;
  }
  return 'center center';
};

export default function Overall() {
  const { t } = useTranslation();
  const session = useSession();
  const userId = session?.user?.id;
  const [rangeDays, setRangeDays] = useState(7);
  const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
  const [isAvatarError, setIsAvatarError] = useState(false);
  const [avatarCacheBust, setAvatarCacheBust] = useState(() => localStorage.getItem('avatarCacheBust') || '');
  const avatarImgRef = useRef(null);

  // Sync cache bust listener
  React.useEffect(() => {
    const syncCacheBust = (event) => {
      if (event?.type === 'storage' && event.key !== 'avatarCacheBust') return;
      const nextValue = localStorage.getItem('avatarCacheBust') || '';
      setAvatarCacheBust(nextValue);
    };
    window.addEventListener('storage', syncCacheBust);
    window.addEventListener('avatar-cache-bust', syncCacheBust);
    return () => {
      window.removeEventListener('storage', syncCacheBust);
      window.removeEventListener('avatar-cache-bust', syncCacheBust);
    };
  }, []);

  const { practiceLanguage } = useUserLanguage(userId);

  const { data: userProfile, isLoading: isLoadingUserProfile } = useQuery({
    queryKey: ['userProfile', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase.from('profiles').select('id, username, avatar, background').eq('id', userId).single();
      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      return data;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const { data: rawRecords, isLoading: isLoadingRecords, isError: isRecordsError } = useQuery({
    queryKey: ['practiceRecords', userId, practiceLanguage],
    queryFn: () => getPracticeRecords(userId, practiceLanguage),
    enabled: !!userId && !!practiceLanguage,
    refetchOnWindowFocus: false,
  });

  const { data: phonemeSummary, isLoading: isLoadingPhoneme, isError: isPhonemeError } = useQuery({
    queryKey: ['phonemeSummary', userId, practiceLanguage],
    queryFn: () => getPhonemeSummary(userId, practiceLanguage),
    enabled: !!userId && !!practiceLanguage,
    refetchOnWindowFocus: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);
  const avatarUrl = useMemo(() => {
    const avatarPath = userProfile?.avatar;
    if (!avatarPath) return '';
    if (!publicBaseUrl) return avatarPath;
    const baseUrl = `${publicBaseUrl}/${avatarPath}`;
    return avatarCacheBust ? `${baseUrl}?v=${avatarCacheBust}` : baseUrl;
  }, [avatarCacheBust, publicBaseUrl, userProfile?.avatar]);
  
  const backgroundUrl = useMemo(() => {
    const backgroundPath = userProfile?.background;
    if (!backgroundPath) return '';
    if (!publicBaseUrl) return backgroundPath;
    return `${publicBaseUrl}/${backgroundPath}`;
  }, [publicBaseUrl, userProfile?.background]);

  const showAvatarImage = Boolean(avatarUrl) && !isAvatarError;
  const showAvatarSkeleton = isLoadingUserProfile || (Boolean(avatarUrl) && !isAvatarLoaded && !isAvatarError);
  const showAvatarFallback = (!avatarUrl && !isLoadingUserProfile) || isAvatarError;

  const chartData = useMemo(
    () => processRecordsForCharts(rawRecords, phonemeSummary, rangeDays, t),
    [rawRecords, phonemeSummary, rangeDays, t]
  );

  const totalPractices = rawRecords ? rawRecords.length : 0;
  const avgCorrectRate = useMemo(() => {
    if (!rawRecords || rawRecords.length === 0) return null;
    const validRates = rawRecords
      .map((rec) => rec.error_rate)
      .filter((rate) => typeof rate === 'number' && !Number.isNaN(rate));
    if (validRates.length === 0) return null;
    const avgErrorRate = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
    const correctRate = 1 - avgErrorRate;
    return Math.max(0, Math.min(1, correctRate));
  }, [rawRecords]);

  const joinDate = session?.user?.created_at
    ? format(parseISO(session.user.created_at), 'MMM d, yyyy')
    : '--';

  const rangeOptions = [
    { value: '7', label: t('recordsPage.last7Days', 'Last 7 days') },
    { value: '14', label: t('recordsPage.last14Days', 'Last 14 days') },
  ];

  const progressChartOptions = {
    chart: {
      id: "progress",
      type: "line",
      toolbar: { show: false },
      background: "transparent",
      zoom: { enabled: false }
    },
    xaxis: {
      type: 'datetime',
      min: chartData.progressChart.minDate,
      max: chartData.progressChart.maxDate,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        datetimeFormatter: {
          year: 'MMM d',
          month: 'MMM d',
          day: 'MMM d',
          hour: 'HH:mm'
        },
        style: {
          colors: '#6b7280',
          fontSize: '12px'
        }
      },
      tooltip: { enabled: false },
      crosshairs: {
        show: true,
        stroke: {
          color: '#bfdbfe',
          width: 1,
          dashArray: 0,
        }
      }
    },
    stroke: {
      curve: "straight",
      width: [2, 2],
      dashArray: [0, 6],
      lineCap: 'round'
    },
    colors: ["#0a66e8", "#0a66e8"],
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      hover: { size: 6 }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.2,
        opacityTo: 0,
        stops: [0, 100],
      },
      opacity: [1, 0],
    },
    legend: {
      show: false,
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 0,
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: { formatter: (val) => val.toFixed(0) + "%" }
    },
    tooltip: {
      shared: true,
      intersect: false,
      marker: { show: true },
      x: { format: 'MMM d' },
      y: { formatter: (val) => (val == null ? 'No record' : `${Number(val).toFixed(1)}%`) }
    }
  };

  const phonemeChartOptions = {
    chart: { id: "phoneme", type: "bar", toolbar: { show: false }, background: "transparent" },
    plotOptions: { bar: { horizontal: true, barHeight: "50%", borderRadius: 4 } },
    xaxis: { categories: chartData.phonemeChart.phonemes },
    colors: ["#f59e0b"],
    grid: { borderColor: "#e2e8f0" }
  };

  const heatmapChartOptions = {
    chart: { type: "heatmap", toolbar: { show: false }, background: "transparent", fontFamily: "'SF Pro Display', sans-serif" },
    plotOptions: { heatmap: { radius: 4, enableShades: false, colorScale: { ranges: [ { from: 0, to: 0, name: t('recordsPage.heatmap.none'), color: "#ebedf0" }, { from: 1, to: 5, name: t('recordsPage.heatmap.low'), color: "#9be9a8" }, { from: 6, to: 10, name: t('recordsPage.heatmap.medium'), color: "#40c463" }, { from: 11, to: 15, name: t('recordsPage.heatmap.high'), color: "#30a14e" }, { from: 16, to: 9999, name: t('recordsPage.heatmap.veryHigh'), color: "#216e39" } ] } } },
    stroke: { width: 2, colors: ["#ffffff"] },
    dataLabels: { enabled: false },
    legend: { position: "top", horizontalAlign: "left", fontSize: '13px', markers: { width: 12, height: 12, radius: 2 }, itemMargin: { horizontal: 10 } },
    xaxis: { type: "category", categories: [t('recordsPage.weekdays.mon'), t('recordsPage.weekdays.tue'), t('recordsPage.weekdays.wed'), t('recordsPage.weekdays.thu'), t('recordsPage.weekdays.fri'), t('recordsPage.weekdays.sat'), t('recordsPage.weekdays.sun')] },
    grid: { show: false },
    tooltip: { theme: 'light', custom: function({ series, seriesIndex, dataPointIndex, w }) { const point = w.globals.initialSeries[seriesIndex].data[dataPointIndex]; if (!point || point.date === null) return ''; const practiceCount = point.y; const date = format(parseISO(point.date), 'MMM d, yyyy'); return `<div class="apexcharts-tooltip-custom"><span>${date}: <b>${practiceCount}</b> ${t('recordsPage.heatmap.practices')}</span></div>`; } }
  };

  const difficultyChartOptions = {
    chart: { type: "donut", background: "transparent" },
    labels: chartData.difficultyChart.levels,
    colors: ["#67e8f9", "#38bdf8", "#0ea5e9", "#0284c7"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true, formatter: (val) => val.toFixed(1) + "%" },
    plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: t('recordsPage.totalPractice') } } } } }
  };

  if (isLoadingRecords || isLoadingPhoneme) {
    return <main className="main-content width-records"><h1 className="page-title">{t('recordsPage.overallTitle', 'Overall')}</h1><Loader /></main>;
  }
  if (isRecordsError || isPhonemeError) {
    return <main className="main-content width-records"><p>{t('recordsPage.errorLoadingRecords')}</p></main>;
  }

  return (
    <main className="main-content width-records">
      <h1 className="page-title">{t('recordsPage.overallTitle', 'Overall')}</h1>
      <section className="overall-profile-card" aria-label={t('recordsPage.overallProfileLabel', 'Overall profile summary')}>
        <div
          className="overall-profile-cover"
          role="img"
          aria-label={t('recordsPage.overallCover', 'Overall cover')}
        >
          {backgroundUrl && (
            <img
              src={backgroundUrl}
              alt=""
              className="overall-profile-cover-img"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: getBackgroundPosition(backgroundUrl)
              }}
            />
          )}
        </div>
        <div className="overall-profile-hero-content">
          <div className="overall-profile-avatar-block">
            <div className="overall-profile-avatar" aria-hidden="true">
              {showAvatarSkeleton && (
                <div className={`overall-profile-avatar-skeleton ${isAvatarLoaded ? 'is-hidden' : ''}`} />
              )}
              {showAvatarImage && (
                <img
                  className={`overall-profile-avatar-img ${isAvatarLoaded ? 'is-visible' : ''}`}
                  src={avatarUrl}
                  alt={t('profilePage.avatarAlt', 'User avatar')}
                  ref={avatarImgRef}
                  key={avatarUrl}
                  onLoad={() => setIsAvatarLoaded(true)}
                  onError={() => setIsAvatarError(true)}
                />
              )}
              {showAvatarFallback && (
                <span className="overall-profile-avatar-initial">
                  {((userProfile?.username || session?.user?.email || 'U') + '').trim().charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <div className="overall-profile-identity">
            <div className="overall-profile-name">
              {userProfile?.username || session?.user?.email || t('recordsPage.unknownUser', 'Unknown user')}
            </div>
            <div className="overall-profile-subtitle">{t('recordsPage.overallSnapshot', 'Overall snapshot')}</div>
          </div>
        </div>
        <div className="overall-profile-stats">
          <div className="overall-profile-stat">
            <div className="overall-profile-stat-label">{t('recordsPage.totalPractice', 'Total practices')}</div>
            <div className="overall-profile-stat-value">{totalPractices}</div>
          </div>
          <div className="overall-profile-stat">
            <div className="overall-profile-stat-label">{t('recordsPage.avgCorrectRate', 'Avg correct rate')}</div>
            <div className="overall-profile-stat-value">
              {avgCorrectRate === null ? '--' : `${(avgCorrectRate * 100).toFixed(1)}%`}
            </div>
          </div>
          <div className="overall-profile-stat">
            <div className="overall-profile-stat-label">{t('recordsPage.joinDate', 'Join date')}</div>
            <div className="overall-profile-stat-value">{joinDate}</div>
          </div>
        </div>
      </section>
      <div className="dashboard-grid">
        <div className="chart-card overall-progress-chart-card">
          <div className="chart-card-header">
            <h3 className="chart-title">{t('recordsPage.overallProgress')}</h3>
            <div className="overall-progress-controls">
              <ModernCombobox
                options={rangeOptions}
                value={String(rangeDays)}
                onChange={(value) => setRangeDays(Number(value))}
                placeholder={t('recordsPage.rangePlaceholder', 'Select range')}
                allowTyping={false}
              />
            </div>
          </div>
          <Chart
            options={progressChartOptions}
            series={[
              { name: t('recordsPage.avgErrorRate'), type: "area", data: chartData.progressChart.fullSeriesData },
              { name: t('recordsPage.avgErrorRate'), type: "line", data: chartData.progressChart.dashedSeriesData },
            ]}
            type="line"
            height={300}
          />
        </div>
        <div className="chart-card"><h3 className="chart-title">{t('recordsPage.frequentErrors')}</h3><Chart options={phonemeChartOptions} series={[{ name: t('recordsPage.errors'), data: chartData.phonemeChart.errorCounts }]} type="bar" height={300} /></div>
        <div className="chart-card"><h3 className="chart-title">{t('recordsPage.weeklyFrequency')}</h3><Chart options={heatmapChartOptions} series={chartData.heatmapChart} type="heatmap" height={300} /></div>
        <div className="chart-card"><h3 className="chart-title">{t('recordsPage.difficultyDistribution')}</h3><Chart options={difficultyChartOptions} series={chartData.difficultyChart.counts} type="donut" height={300} /></div>
      </div>
    </main>
  );
}
